/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter 
} from "../ui/card";
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, 
  DialogFooter, DialogDescription 
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue
} from "../ui/select";
import { 
  Loader2, AlertTriangle, CheckCircle2,
  PlusCircle, Edit, Trash,
  Users, RefreshCcw, BookCopy, Calendar,
  Archive, ArchiveRestore
} from "lucide-react";
import { useToast } from "../ui/use-toast";

const ClassEditor = ({ teacherContext = false, teacherId = null }) => {
  const [classes, setClasses] = useState([]);
  const [archivedClasses, setArchivedClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState("classes");
  
  // State for class operations
  const [isCreatingClass, setIsCreatingClass] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [deletingClass, setDeletingClass] = useState(null);
  const [deletionImpact, setDeletionImpact] = useState(null);
  const [showDeletionWarning, setShowDeletionWarning] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [assigningClass, setAssigningClass] = useState(null);
  
  // Form state
  const [classForm, setClassForm] = useState({
    name: '',
    description: ''
  });
  
  // Assignment state
  const [availableStudents, setAvailableStudents] = useState([]);
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');

  // Quick test assignment state
  const [availableTests, setAvailableTests] = useState([]);
  const [assignedTests, setAssignedTests] = useState([]);
  const [assignedTestSearchTerm, setAssignedTestSearchTerm] = useState('');
  const [availableTestSearchTerm, setAvailableTestSearchTerm] = useState('');
  const [availableTestCategoryFilter, setAvailableTestCategoryFilter] = useState('all');
  
  const { toast } = useToast();

  // Fetch classes with stats
  const fetchClasses = useCallback(async () => {
    try {
      setLoading(true);
      
      let cohorts = [];
      
      if (teacherContext && teacherId) {
        // Fetch teacher's assigned cohorts (from cohort_teachers table)
        const [assignedCohortsResponse, progressResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/teacher/cohorts`, {
            credentials: 'include'
          }),
          fetch(`${process.env.REACT_APP_API_URL}/api/teacher/students-progress`, {
            credentials: 'include'
          })
        ]);

        if (!assignedCohortsResponse.ok || !progressResponse.ok) {
          throw new Error('Failed to fetch teacher data');
        }

        const assignedCohorts = await assignedCohortsResponse.json();
        
        // Only show teacher's formally assigned cohorts
        cohorts = assignedCohorts;
      } else {
        // Admin view - fetch all cohorts
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cohorts`, {
          credentials: 'include'
        });
        
        if (!response.ok) throw new Error('Failed to fetch classes');
        
        cohorts = await response.json();
      }
      
      // Fetch stats for each class
      const classesWithStats = await Promise.all(
        cohorts.map(async (cohort) => {
          try {
            const [studentsRes, teachersRes] = await Promise.all([
              fetch(`${process.env.REACT_APP_API_URL}/api/cohorts/${cohort.id}/users`, {
                credentials: 'include'
              }),
              fetch(`${process.env.REACT_APP_API_URL}/api/cohorts/${cohort.id}/teachers`, {
                credentials: 'include'
              })
            ]);
            
            const students = studentsRes.ok ? await studentsRes.json() : [];
            const teachers = teachersRes.ok ? await teachersRes.json() : [];
            
            return {
              ...cohort,
              studentCount: students.length,
              teacherCount: teachers.length,
              students,
              teachers
            };
          } catch (error) {
            console.error(`Error fetching stats for class ${cohort.id}:`, error);
            return {
              ...cohort,
              studentCount: 0,
              teacherCount: 0,
              students: [],
              teachers: []
            };
          }
        })
      );
      
      setClasses(classesWithStats);
    } catch (error) {
      console.error('Error fetching classes:', error);
      setError('Failed to load classes. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [teacherContext, teacherId]);

  // Fetch archived classes
  const fetchArchivedClasses = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cohorts/archived`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const archived = await response.json();
        setArchivedClasses(archived);
      } else {
        console.warn('Failed to fetch archived classes');
        setArchivedClasses([]);
      }
    } catch (error) {
      console.error('Error fetching archived classes:', error);
      setArchivedClasses([]);
    }
  }, []);

  // Archive a class
  const handleArchiveClass = async (classItem) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cohorts/${classItem.id}/archive`, 
        {
          method: 'PATCH',
          credentials: 'include'
        }
      );

      if (response.ok) {
        toast({
          title: "Class Archived",
          description: `"${classItem.name}" has been archived successfully.`,
        });
        
        // Refresh both active and archived classes
        fetchClasses();
        fetchArchivedClasses();
      } else {
        const errorData = await response.json();
        toast({
          title: "Archive Failed",
          description: errorData.error || "Failed to archive class",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error archiving class:', error);
      toast({
        title: "Archive Failed",
        description: "An error occurred while archiving the class",
        variant: "destructive",
      });
    }
  };

  // Unarchive a class
  const handleUnarchiveClass = async (classItem) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cohorts/${classItem.id}/unarchive`, 
        {
          method: 'PATCH',
          credentials: 'include'
        }
      );

      if (response.ok) {
        toast({
          title: "Class Restored",
          description: `"${classItem.name}" has been restored from archive.`,
        });
        
        // Refresh both active and archived classes
        fetchClasses();
        fetchArchivedClasses();
      } else {
        const errorData = await response.json();
        toast({
          title: "Restore Failed",
          description: errorData.error || "Failed to restore class",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error restoring class:', error);
      toast({
        title: "Restore Failed",
        description: "An error occurred while restoring the class",
        variant: "destructive",
      });
    }
  };

  // Fetch available students and teachers
  const fetchAssignmentData = useCallback(async () => {
    try {
      const [studentsRes, teachersRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/users`, {
          credentials: 'include'
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/teachers`, {
          credentials: 'include'
        })
      ]);
      
      if (studentsRes.ok) {
        const students = await studentsRes.json();
        setAvailableStudents(students.filter(user => !user.is_admin));
      }
      
      if (teachersRes.ok) {
        const teachers = await teachersRes.json();
        setAvailableTeachers(teachers);
      }
    } catch (error) {
      console.error('Error fetching assignment data:', error);
    }
  }, []);

  // Fetch available tests and current test assignments for a cohort
  const fetchTestsForCohort = useCallback(async (cohortId) => {
    try {
      const [testsRes, assignmentsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/${teacherContext ? 'teacher' : 'admin'}/tests`, {
          credentials: 'include'
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/${teacherContext ? 'teacher/tests/assignments' : 'tests/cohort-assignments'}?cohortId=${cohortId}`, {
          credentials: 'include'
        })
      ]);

      if (testsRes.ok) {
        const tests = await testsRes.json();
        setAvailableTests(tests.filter(test => test.is_active));
      } else {
        console.error('Failed to fetch tests:', testsRes.status, testsRes.statusText);
      }

      if (assignmentsRes.ok) {
        const assignments = await assignmentsRes.json();
        setAssignedTests(assignments);
      } else {
        console.error('Failed to fetch assignments:', assignmentsRes.status, assignmentsRes.statusText);
        setAssignedTests([]);
      }
    } catch (error) {
      console.error('Error fetching tests for cohort:', error);
      setAssignedTests([]);
      setAvailableTests([]);
    }
  }, [teacherContext]);

  // Cohort-specific function to update class assignments
  const updateClassAssignments = async (testIds) => {
    if (!assigningClass) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/${teacherContext ? 'teacher' : 'admin'}/cohorts/${assigningClass.id}/test-assignments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ testIds })
      });

      if (response.ok) {
        // Refresh test assignments
        fetchTestsForCohort(assigningClass.id);
        return true;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update test assignments');
      }
    } catch (error) {
      console.error('Error updating test assignments:', error);
      toast({
        title: "Assignment Update Failed",
        description: error.message || "Failed to update test assignments",
        variant: "destructive"
      });
      return false;
    }
  };

  // Quick test assignment handler
  const handleQuickTestAssign = async (testId) => {
    if (!assigningClass) return;

    try {
      const currentTestIds = assignedTests.map(t => t.test_id);
      const newTestIds = [...currentTestIds, testId];

      const success = await updateClassAssignments(newTestIds);
      if (success) {
        toast({
          title: "Test Assigned",
          description: `Test assigned to ${assigningClass.name} successfully`,
        });
      }
    } catch (error) {
      console.error('Error assigning test:', error);
    }
  };

  // Quick test unassign handler - removes test from current assignments
  const handleQuickTestUnassign = async (testId) => {
    if (!assigningClass) return;

    try {
      const currentTestIds = assignedTests.map(t => t.test_id);
      const newTestIds = currentTestIds.filter(id => id !== testId);

      const success = await updateClassAssignments(newTestIds);
      if (success) {
        toast({
          title: "Test Unassigned",
          description: `Test removed from ${assigningClass.name} successfully`,
        });
      }
    } catch (error) {
      console.error('Error unassigning test:', error);
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchArchivedClasses();
    fetchAssignmentData();
  }, [fetchClasses, fetchArchivedClasses, fetchAssignmentData]);

  // Create new class
  const handleCreateClass = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cohorts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(classForm)
      });

      if (!response.ok) throw new Error('Failed to create class');

      toast({
        title: "Success",
        description: "Class created successfully",
      });

      setIsCreatingClass(false);
      setClassForm({ name: '', description: '' });
      fetchClasses();
    } catch (error) {
      console.error('Error creating class:', error);
      toast({
        title: "Error",
        description: "Failed to create class. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Update class
  const handleUpdateClass = async () => {
    if (!editingClass) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cohorts/${editingClass.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: classForm.name,
          description: classForm.description
        })
      });

      if (!response.ok) throw new Error('Failed to update class');

      toast({
        title: "Success",
        description: "Class updated successfully",
      });

      setEditingClass(null);
      setClassForm({ name: '', description: '' });
      fetchClasses();
    } catch (error) {
      console.error('Error updating class:', error);
      toast({
        title: "Error",
        description: "Failed to update class. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Fetch deletion impact assessment
  const fetchDeletionImpact = async (classToDelete) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cohorts/${classToDelete.id}/deletion-impact`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch deletion impact');

      const impact = await response.json();
      setDeletionImpact(impact);
      setDeletingClass(classToDelete);
      setShowDeletionWarning(true);
    } catch (error) {
      console.error('Error fetching deletion impact:', error);
      toast({
        title: "Error",
        description: "Failed to assess deletion impact. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Initiate class deletion (now shows impact first)
  const handleInitiateDelete = (classToDelete) => {
    fetchDeletionImpact(classToDelete);
  };

  // Actually delete the class (after confirmation)
  const handleConfirmDelete = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cohorts/${deletingClass.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete class');

      toast({
        title: "Success",
        description: "Class deleted successfully",
      });

      setShowDeletionWarning(false);
      setDeletingClass(null);
      setDeletionImpact(null);
      setConfirmationText('');
      fetchClasses();
    } catch (error) {
      console.error('Error deleting class:', error);
      toast({
        title: "Error",
        description: "Failed to delete class. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Cancel deletion
  const handleCancelDelete = () => {
    setShowDeletionWarning(false);
    setDeletingClass(null);
    setDeletionImpact(null);
    setConfirmationText('');
  };

  // Handle edit class
  const handleEditClass = (classItem) => {
    setEditingClass(classItem);
    setClassForm({
      name: classItem.name,
      description: classItem.description || ''
    });
  };

  // Handle assign students/teachers
  const handleAssignToClass = async (classItem) => {
    setAssigningClass(classItem);
    setSelectedStudents(classItem.students.map(s => s.id));
    setSelectedTeachers(classItem.teachers.map(t => t.id));

    // Clear search terms
    setAssignedTestSearchTerm('');
    setAvailableTestSearchTerm('');
    setStudentSearchTerm('');

    // Fetch tests for this cohort
    fetchTestsForCohort(classItem.id);
  };

  // Update assignments
  const handleUpdateAssignments = async () => {
    if (!assigningClass) return;
    
    try {
      // Update students
      const studentsResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cohorts/${assigningClass.id}/users`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            userIds: selectedStudents,
            cohortName: assigningClass.name
          })
        }
      );

      // Update teachers
      const teachersResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cohorts/${assigningClass.id}/teachers`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            teacherIds: selectedTeachers
          })
        }
      );

      if (!studentsResponse.ok || !teachersResponse.ok) {
        throw new Error('Failed to update assignments');
      }

      toast({
        title: "Success",
        description: "Assignments updated successfully",
      });

      setAssigningClass(null);
      setSelectedStudents([]);
      setSelectedTeachers([]);
      fetchClasses();
    } catch (error) {
      console.error('Error updating assignments:', error);
      toast({
        title: "Error",
        description: "Failed to update assignments. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Filter classes based on search
  const filteredClasses = classes.filter(classItem =>
    classItem.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (classItem.description && classItem.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Filter students for assignment
  const filteredStudents = availableStudents.filter(student =>
    student.first_name.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
    student.last_name.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
    student.student_id.includes(studentSearchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading classes...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Title and Actions */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Class Management</h1>
          <p className="text-muted-foreground">
            {teacherContext ? 'Manage your classes and student assignments' : 'Manage school classes and assignments'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => {
              setClassForm({ name: '', description: '' });
              setIsCreatingClass(true);
            }}
            className="gap-2"
          >
            <PlusCircle className="h-4 w-4" />
            Create Class
          </Button>
          <Button variant="outline" onClick={fetchClasses} className="gap-2">
            <RefreshCcw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Input
          placeholder="Search classes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Classes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{classes.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Students</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {classes.reduce((sum, c) => sum + c.studentCount, 0)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Teachers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(classes.flatMap(c => c.teachers.map(t => t.id))).size}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Avg Class Size</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {classes.length > 0 ? Math.round(classes.reduce((sum, c) => sum + c.studentCount, 0) / classes.length) : 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Classes and Archive Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="classes">
            Active Classes ({classes.length})
          </TabsTrigger>
          <TabsTrigger value="archived">
            Archived ({archivedClasses.length})
          </TabsTrigger>
        </TabsList>

        {/* Active Classes Tab */}
        <TabsContent value="classes" className="space-y-6">
          {/* Classes Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClasses.map((classItem) => (
              <Card key={classItem.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{classItem.name}</CardTitle>
                      <CardDescription className="line-clamp-2">
                        {classItem.description || 'No description provided'}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Student Count */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Students</span>
                      </div>
                      <Badge variant="secondary">
                        {classItem.studentCount}
                      </Badge>
                    </div>
                    
                    {/* Teacher Count */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <BookCopy className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Teachers</span>
                      </div>
                      <Badge variant="outline">
                        {classItem.teacherCount}
                      </Badge>
                    </div>
                    
                    {/* Created Date */}
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      Created {new Date(classItem.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex gap-2 pt-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleEditClass(classItem)}
                    className="flex-1"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleAssignToClass(classItem)}
                    className="flex-1"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Assign
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleArchiveClass(classItem)}
                    className="text-orange-600 hover:text-orange-700"
                    title="Archive Class"
                  >
                    <Archive className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleInitiateDelete(classItem)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* Empty State for Active Classes */}
          {filteredClasses.length === 0 && !loading && (
            <div className="text-center py-12">
              <BookCopy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No active classes found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm ? 'Try adjusting your search criteria' : 'Create your first class to get started'}
              </p>
              {!searchTerm && (
                <Button onClick={() => setIsCreatingClass(true)}>
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Create First Class
                </Button>
              )}
            </div>
          )}
        </TabsContent>

        {/* Archived Classes Tab */}
        <TabsContent value="archived" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {archivedClasses.map((classItem) => (
              <Card key={classItem.id} className="hover:shadow-md transition-shadow opacity-75">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{classItem.name}</CardTitle>
                      <CardDescription className="line-clamp-2">
                        {classItem.description || 'No description provided'}
                      </CardDescription>
                    </div>
                    <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                      Archived
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Archived Info */}
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Archive className="h-3 w-3" />
                      Archived {new Date(classItem.archived_at).toLocaleDateString()} by {classItem.archived_by_name}
                    </div>
                    
                    {/* Created Date */}
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      Created {new Date(classItem.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex gap-2 pt-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleUnarchiveClass(classItem)}
                    className="flex-1 text-green-600 hover:text-green-700"
                  >
                    <ArchiveRestore className="h-4 w-4 mr-2" />
                    Restore
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleInitiateDelete(classItem)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* Empty State for Archived Classes */}
          {archivedClasses.length === 0 && !loading && (
            <div className="text-center py-12">
              <Archive className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No archived classes</h3>
              <p className="text-muted-foreground">
                Classes you archive will appear here and can be restored later.
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Class Dialog */}
      <Dialog open={isCreatingClass} onOpenChange={setIsCreatingClass}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Class</DialogTitle>
            <DialogDescription>
              Add a new class to organize your students and assignments.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="className">Class Name</Label>
              <Input
                id="className"
                value={classForm.name}
                onChange={(e) => setClassForm({...classForm, name: e.target.value})}
                placeholder="Enter class name (e.g., Wood Shop 101)"
              />
            </div>
            <div>
              <Label htmlFor="classDescription">Description (Optional)</Label>
              <Textarea
                id="classDescription"
                value={classForm.description}
                onChange={(e) => setClassForm({...classForm, description: e.target.value})}
                placeholder="Enter class description..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreatingClass(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateClass} disabled={!classForm.name.trim()}>
              Create Class
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Class Dialog */}
      <Dialog open={!!editingClass} onOpenChange={() => setEditingClass(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Class</DialogTitle>
            <DialogDescription>
              Update class information and settings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="editClassName">Class Name</Label>
              <Input
                id="editClassName"
                value={classForm.name}
                onChange={(e) => setClassForm({...classForm, name: e.target.value})}
                placeholder="Enter class name"
              />
            </div>
            <div>
              <Label htmlFor="editClassDescription">Description</Label>
              <Textarea
                id="editClassDescription"
                value={classForm.description}
                onChange={(e) => setClassForm({...classForm, description: e.target.value})}
                placeholder="Enter class description..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingClass(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateClass} disabled={!classForm.name.trim()}>
              Update Class
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comprehensive Deletion Warning Dialog */}
      <Dialog open={showDeletionWarning} onOpenChange={handleCancelDelete}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Delete Class: {deletingClass?.name}
            </DialogTitle>
            <DialogDescription>
              <strong className="text-red-700">⚠️ CRITICAL WARNING:</strong> This action will permanently delete the class and ALL associated data. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {deletionImpact && (
            <div className="space-y-4 py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-800 mb-3">Impact Assessment:</h4>
                
                {/* Students Impact */}
                {deletionImpact.students.count > 0 && (
                  <div className="mb-3">
                    <p className="font-medium text-red-700">
                      📚 {deletionImpact.students.count} Student{deletionImpact.students.count !== 1 ? 's' : ''} Affected:
                    </p>
                    <ul className="text-sm text-red-600 ml-4 mt-1">
                      {deletionImpact.students.names.slice(0, 10).map((name, idx) => (
                        <li key={idx}>• {name}</li>
                      ))}
                      {deletionImpact.students.names.length > 10 && (
                        <li>• ... and {deletionImpact.students.names.length - 10} more</li>
                      )}
                    </ul>
                  </div>
                )}

                {/* Teachers Impact */}
                {deletionImpact.teachers.count > 0 && (
                  <div className="mb-3">
                    <p className="font-medium text-red-700">
                      👨‍🏫 {deletionImpact.teachers.count} Teacher{deletionImpact.teachers.count !== 1 ? 's' : ''} Assigned:
                    </p>
                    <ul className="text-sm text-red-600 ml-4 mt-1">
                      {deletionImpact.teachers.names.map((name, idx) => (
                        <li key={idx}>• {name}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Test Assignments Impact */}
                {deletionImpact.testAssignments.count > 0 && (
                  <div className="mb-3">
                    <p className="font-medium text-red-700">
                      📝 {deletionImpact.testAssignments.count} Test Assignment{deletionImpact.testAssignments.count !== 1 ? 's' : ''}:
                    </p>
                    <ul className="text-sm text-red-600 ml-4 mt-1">
                      {deletionImpact.testAssignments.testNames.slice(0, 5).map((name, idx) => (
                        <li key={idx}>• {name}</li>
                      ))}
                      {deletionImpact.testAssignments.testNames.length > 5 && (
                        <li>• ... and {deletionImpact.testAssignments.testNames.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}

                {/* Test Results Impact */}
                {deletionImpact.testResults.count > 0 && (
                  <div className="mb-3">
                    <p className="font-medium text-red-700">
                      📊 {deletionImpact.testResults.count} Test Result{deletionImpact.testResults.count !== 1 ? 's' : ''} Will Be Lost
                    </p>
                    <p className="text-sm text-red-600 ml-4">All student progress and scores in this class</p>
                  </div>
                )}

                {/* Safe to delete message */}
                {deletionImpact.students.count === 0 && 
                 deletionImpact.teachers.count === 0 && 
                 deletionImpact.testAssignments.count === 0 && 
                 deletionImpact.testResults.count === 0 && (
                  <div className="text-green-700">
                    ✅ This class appears to be empty with no associated data.
                  </div>
                )}
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">What will happen:</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• The class "{deletingClass?.name}" will be permanently deleted</li>
                  <li>• Students will be removed from this class (they can re-register if needed)</li>
                  <li>• Teacher assignments to this class will be removed</li>
                  <li>• All test assignments for this class will be deleted</li>
                  <li>• Student test progress in this class will be preserved in their individual records</li>
                </ul>
              </div>

              <div className="bg-red-100 border border-red-300 rounded-lg p-4">
                <p className="text-red-800 font-medium">
                  Type the class name "<strong>{deletingClass?.name}</strong>" below to confirm deletion:
                </p>
                <Input
                  className="mt-2"
                  placeholder={`Type "${deletingClass?.name}" to confirm`}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  value={confirmationText}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={handleCancelDelete}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={confirmationText !== deletingClass?.name}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash className="h-4 w-4 mr-2" />
              Permanently Delete Class
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment Dialog */}
      {assigningClass && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-[95vw] h-[95vh] max-w-none flex flex-col shadow-lg">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Users className="h-5 w-5" />
                Manage Assignments - {assigningClass.name}
              </h2>
              <p className="text-muted-foreground mt-1">
                Assign teachers, students, and tests to this class. Changes are saved automatically.
              </p>
            </div>
          
            <div className="flex-1 p-6 overflow-y-auto">
              <Tabs defaultValue="students" className="flex-1 flex flex-col">
            <TabsList className="grid w-full grid-cols-3 mb-4 bg-gray-100 p-1 rounded-lg">
              <TabsTrigger
                value="students"
                className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 font-semibold rounded-md py-2"
              >
                Students ({selectedStudents.length})
              </TabsTrigger>
              <TabsTrigger
                value="teachers"
                className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 font-semibold rounded-md py-2"
              >
                Teachers ({selectedTeachers.length})
              </TabsTrigger>
              <TabsTrigger
                value="tests"
                className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 font-semibold rounded-md py-2"
              >
                Tests ({assignedTests.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="students" className="mt-4 space-y-4">
              <div className="flex gap-4 items-center">
                <Input
                  placeholder="Search students by name or ID..."
                  value={studentSearchTerm}
                  onChange={(e) => setStudentSearchTerm(e.target.value)}
                  className="flex-1"
                />
                <Badge variant="secondary">
                  {filteredStudents.length} available
                </Badge>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-72">
                {/* Available Students */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Available Students</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="max-h-80 overflow-y-auto">
                      {filteredStudents
                        .filter(student => !selectedStudents.includes(student.id))
                        .map((student) => (
                          <div 
                            key={student.id} 
                            className="flex items-center justify-between p-3 hover:bg-gray-50 border-b last:border-b-0"
                          >
                            <div className="flex-1">
                              <div className="font-medium">
                                {student.first_name} {student.last_name}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                ID: {student.student_id}
                                {student.shop_class && (
                                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                    {student.shop_class}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setSelectedStudents(prev => [...prev, student.id])}
                            >
                              Add
                            </Button>
                          </div>
                        ))}
                      {filteredStudents.filter(s => !selectedStudents.includes(s.id)).length === 0 && (
                        <div className="p-8 text-center text-muted-foreground">
                          <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>No available students</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Selected Students */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Assigned Students</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="max-h-80 overflow-y-auto">
                      {availableStudents
                        .filter(student => selectedStudents.includes(student.id))
                        .map((student) => (
                          <div 
                            key={student.id} 
                            className="flex items-center justify-between p-3 hover:bg-green-50 border-b last:border-b-0 bg-green-25"
                          >
                            <div className="flex-1">
                              <div className="font-medium">
                                {student.first_name} {student.last_name}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                ID: {student.student_id}
                              </div>
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setSelectedStudents(prev => prev.filter(id => id !== student.id))}
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                      {selectedStudents.length === 0 && (
                        <div className="p-8 text-center text-muted-foreground">
                          <CheckCircle2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>No students assigned</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="teachers" className="mt-4 space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-72">
                {/* Available Teachers */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Available Teachers</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="max-h-80 overflow-y-auto">
                      {availableTeachers
                        .filter(teacher => !selectedTeachers.includes(teacher.id))
                        .map((teacher) => (
                          <div 
                            key={teacher.id} 
                            className="flex items-center justify-between p-3 hover:bg-gray-50 border-b last:border-b-0"
                          >
                            <div className="flex-1">
                              <div className="font-medium">
                                {teacher.first_name} {teacher.last_name}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Role: {teacher.role || 'Teacher'}
                              </div>
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setSelectedTeachers(prev => [...prev, teacher.id])}
                            >
                              Assign
                            </Button>
                          </div>
                        ))}
                      {availableTeachers.filter(t => !selectedTeachers.includes(t.id)).length === 0 && (
                        <div className="p-8 text-center text-muted-foreground">
                          <BookCopy className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>No available teachers</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Selected Teachers */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Assigned Teachers</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="max-h-80 overflow-y-auto">
                      {availableTeachers
                        .filter(teacher => selectedTeachers.includes(teacher.id))
                        .map((teacher) => (
                          <div 
                            key={teacher.id} 
                            className="flex items-center justify-between p-3 hover:bg-blue-50 border-b last:border-b-0 bg-blue-25"
                          >
                            <div className="flex-1">
                              <div className="font-medium">
                                {teacher.first_name} {teacher.last_name}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Role: {teacher.role || 'Teacher'}
                              </div>
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setSelectedTeachers(prev => prev.filter(id => id !== teacher.id))}
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                      {selectedTeachers.length === 0 && (
                        <div className="p-8 text-center text-muted-foreground">
                          <CheckCircle2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>No teachers assigned</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="tests" className="mt-4 space-y-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <BookCopy className="h-5 w-5 text-blue-600" />
                  Test Assignments
                </h3>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                    ✅ {assignedTests.length} assigned
                  </Badge>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                    📚 {availableTests.filter(test => !assignedTests.some(assigned => assigned.test_id === test.id)).length} available
                  </Badge>
                </div>
              </div>

              {/* Assigned Tests Section - Primary Focus */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-green-800 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Currently Assigned Tests
                  </h4>
                  {assignedTests.length > 0 && (
                    <Input
                      placeholder="Search assigned tests..."
                      value={assignedTestSearchTerm}
                      onChange={(e) => setAssignedTestSearchTerm(e.target.value)}
                      className="w-64 h-9"
                    />
                  )}
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg">
                  {assignedTests.length === 0 ? (
                    <div className="p-8 text-center text-green-700">
                      <BookCopy className="h-12 w-12 mx-auto mb-3 opacity-60" />
                      <p className="font-medium">No tests assigned yet</p>
                      <p className="text-sm mt-1 opacity-80">Assign tests from the available list below</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-green-200">
                      {assignedTests
                        .filter(test =>
                          assignedTestSearchTerm === '' ||
                          test.test_name.toLowerCase().includes(assignedTestSearchTerm.toLowerCase())
                        )
                        .map((assignment) => (
                          <div key={assignment.test_id} className="p-4 flex items-center justify-between hover:bg-green-100 transition-colors">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                                <div>
                                  <h5 className="font-medium text-green-800">{assignment.test_name}</h5>
                                  <p className="text-sm text-green-600">Assigned to {assignment.cohort_name}</p>
                                </div>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleQuickTestUnassign(assignment.test_id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Available Tests Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                    <BookCopy className="h-4 w-4" />
                    Available Tests to Assign
                  </h4>
                  <div className="flex items-center gap-2">
                    <Select value={availableTestCategoryFilter} onValueChange={setAvailableTestCategoryFilter}>
                      <SelectTrigger className="w-32 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        <SelectItem value="woodshop">Woodshop</SelectItem>
                        <SelectItem value="auto">Auto</SelectItem>
                        <SelectItem value="metal">Metal</SelectItem>
                        <SelectItem value="general">General</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Search available tests..."
                      value={availableTestSearchTerm}
                      onChange={(e) => setAvailableTestSearchTerm(e.target.value)}
                      className="w-64 h-9"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg">
                  {availableTests.filter(test => !assignedTests.some(assigned => assigned.test_id === test.id)).length === 0 ? (
                    <div className="p-8 text-center text-blue-700">
                      <BookCopy className="h-12 w-12 mx-auto mb-3 opacity-60" />
                      <p className="font-medium">All tests assigned</p>
                      <p className="text-sm mt-1 opacity-80">This class has been assigned all available tests</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-blue-200">
                      {availableTests
                        .filter(test => !assignedTests.some(assigned => assigned.test_id === test.id))
                        .filter(test =>
                          availableTestSearchTerm === '' ||
                          test.name.toLowerCase().includes(availableTestSearchTerm.toLowerCase())
                        )
                        .filter(test =>
                          availableTestCategoryFilter === 'all' ||
                          test.category === availableTestCategoryFilter
                        )
                        .map((test) => (
                          <div key={test.id} className="p-4 flex items-center justify-between hover:bg-blue-100 transition-colors">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                                <div>
                                  <h5 className="font-medium text-blue-800">{test.name}</h5>
                                  <p className="text-sm text-blue-600">
                                    {test.description || 'No description available'}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleQuickTestAssign(test.id)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              Assign
                            </Button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
            </div>

            <div className="p-6 border-t flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                Students: {selectedStudents.length} • Teachers: {selectedTeachers.length} • Tests: {assignedTests.length}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAssigningClass(null)}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateAssignments}>
                  Save Assignments
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassEditor;